import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { ChecklistItem } from '../types';

interface CompletedAccordionProps {
  items: ChecklistItem[];
  onItemClick: (item: ChecklistItem) => void;
}

export function CompletedAccordion({ items, onItemClick }: CompletedAccordionProps) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  if (items.length === 0) return null;

  return (
    <div className="border-t border-gray-200">
      {/* Accordion Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
            <Check className="w-4 h-4 text-green-600" />
          </div>
          <span className="font-medium text-gray-900">
            {t('checklists.completedSection', 'Completed Items')}
          </span>
          <span className="px-2.5 py-0.5 bg-green-100 text-green-700 text-sm font-medium rounded-full">
            {items.length}
          </span>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Accordion Content with smooth animation */}
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="divide-y divide-gray-100 bg-gray-50">
            {items.map((item, index) => (
              <button
                key={item.id}
                onClick={() => onItemClick(item)}
                className="w-full grid grid-cols-12 gap-2 px-4 py-3 text-left opacity-70 hover:opacity-100 hover:bg-gray-100 transition-all animate-fade-in"
                style={{ animationDelay: `${index * 30}ms` }}
              >
                {/* Check icon */}
                <div className="col-span-1 flex items-center">
                  <Check className="w-5 h-5 text-green-500" />
                </div>

                {/* Product Code */}
                <div className="col-span-2 text-sm text-gray-400 font-mono truncate">
                  {item.productCode || '-'}
                </div>

                {/* Product Name (strikethrough) */}
                <div className="col-span-6 flex items-center">
                  <span className="font-medium text-gray-500 line-through truncate">
                    {item.name}
                  </span>
                </div>

                {/* Quantity */}
                <div className="col-span-3 text-sm text-gray-400">
                  {item.quantityPurchased || 0}/{item.quantityNeeded}
                  <span className="ml-2 text-green-600">
                    - {t('checklists.installed', 'Installed')}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
